import moment from "moment-timezone";
import React from "react";
import { TooltipProps } from "recharts";
import styled, { css } from "styled-components";
import _ from "lodash";

import { formatPrice } from "src/core/prices/utils";
import { minutesToHHMM } from "src/library/utils/time-helpers/time-helpers";

import { Text } from "../Text";

export interface GraphPayload {
  /** Optional header displayed if provided */
  toolTipHeader?: string;
  /** If toolTipHeader not provided, date looked for and formatted if provided */
  date?: string;
}

export type GraphToolTipValueType = "text" | "number" | "price" | "time";

interface GraphToolTipProps extends TooltipProps<number, string> {
  valueType?: GraphToolTipValueType;
}

// Renders a tool tip shown when hovering a datapoint on a graph
const GraphToolTip: React.FC<GraphToolTipProps> = ({
  active, payload, valueType = "text"
}) => {
  const toolTipPayload: GraphPayload | null = payload && payload[ 0 ] ? payload[ 0 ].payload : null;

  // Based on the value type, format the item value
  const formatItemValue = React.useCallback((value: unknown) => {
    switch (valueType) {
      case "text": {
        return typeof value === "string" ? _.startCase(value.replace(/[_-]/g, " ")) : value;
      }
      case "number": {
        return typeof value === "string" ? parseInt(value) : value;
      }
      case "price": {
        return typeof value === "number" ? formatPrice(value, 2) : value;
      }
      case "time": {
        return typeof value === "number" ? minutesToHHMM(value, "long") : value;
      }
      default:
        return value;
    }
  }, [ valueType ]);

  if (active && payload && payload.length && payload[ 0 ]) {
    return (
      <StyledGraphToolTipWrapper>
        <div className="tooltip-head">
          <Text
            as="h5"
            textTransform="capitalize"
            fontSize="h5"
            weight="bold"
            color="white"
          >
            {toolTipPayload?.toolTipHeader ?
              toolTipPayload?.toolTipHeader : toolTipPayload?.date && moment.utc(toolTipPayload.date).format("MMMM YYYY")}
          </Text>
        </div>

        <div className="tooltip-body">
          {payload.map(item => (
            <Text
              key={item.name}
              fontSize="p"
              color="white"
            >
              {`${item.name && _.startCase(item.name.replace(/[_-]/g, " "))}: ${formatItemValue(item.value)}`}
            </Text>
          ))}
        </div>
      </StyledGraphToolTipWrapper>
    );
  }

  return null;
};

const StyledGraphToolTipWrapper = styled.div`
  ${({ theme }) => css`
    border-radius: ${theme.borderRadius};

    .tooltip-head {
      border-radius: ${theme.borderRadius} ${theme.borderRadius} 0 0;
      background: ${theme.colors.primaryDark};
      padding: ${theme.spacing.padding.sm};
    }

    .tooltip-body {
      border-radius: 0 0 ${theme.borderRadius} ${theme.borderRadius};
      background: ${theme.colors.primaryLight};
      padding: ${theme.spacing.padding.sm};
      span {
        display: block;
      }
    }
  `}
`;

export default GraphToolTip;